import { useEffect, useMemo, useState } from "react";
import { BrandIcon } from "../../components/BrandIcon";
import type { ConnectorDTO, PersonSpendDTO, UserDTO } from "../../../shared/types";
import { BrandTile } from "../../components/BrandIcon";
import { List, ListRow, listRowCls } from "../../components/List";
import { alertDialog, confirmDialog, Dialog } from "../../components/Dialog";
import { CostBreakdown } from "../../components/CostBreakdown";
import { Tokens } from "../../components/Tokens";
import { policySummary } from "../../components/PolicyEditor";
import { ConnectedChannels } from "../../components/ConnectedChannels";
import { Table } from "../../components/Table";
import { TextLink } from "../../components/TextLink";
import { Avatar, Badge, Button, ButtonLink, ErrorText, Field, inputCls, PageHeader, timeAgo } from "../../components/ui";
import type { PresenceDTO } from "../../../shared/types";
import { del, get, post, put } from "../../lib/api";
import { Switch } from "../../components/Switch";

type UserWithAccess = UserDTO & { connectorAccess: Record<string, string> };

type DirectoryStatus = { available: boolean; connectorId: string | null; connectorName: string | null; needsReconnect: boolean; reason: string | null };
type DirectoryUser = {
  email: string; name: string; orgUnitPath: string | null; isAdmin: boolean; suspended: boolean; lastLoginTime: string | null; photoUrl: string | null;
  status: "new" | "existing" | "suspended"; existingUserId: string | null;
};
type ImportResult = { created: UserDTO[]; skippedExisting: string[]; skippedNotFound: string[] };

type HrStatus = { available: boolean; connectorId: string | null; connectorName: string | null; reason: string | null; lastSyncedAt: string | null };
type HrPerson = {
  employeeId: string; email: string; name: string; jobTitle: string | null; department: string | null; location: string | null; manager: string | null; photoUrl: string | null;
  status: "new" | "update" | "unchanged"; existingUserId: string | null; changes: string[];
};
type HrSyncResult = { created: UserDTO[]; updated: string[]; unchanged: number; skippedNotFound: string[] };

function GoogleImport({ status, onDone, onClose }: { status: DirectoryStatus; onDone: () => void; onClose: () => void }) {
  const [users, setUsers] = useState<DirectoryUser[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [q, setQ] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  useEffect(() => {
    post<{ users: DirectoryUser[] }>("/api/integrations/google/directory/preview").then(
      (r) => {
        setUsers(r.users);
        setSelected(new Set(r.users.filter((u) => u.status === "new").map((u) => u.email)));
      },
      (err) => setError((err as Error).message),
    );
  }, []);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (users ?? []).filter((u) => !needle || u.email.includes(needle) || u.name.toLowerCase().includes(needle) || (u.orgUnitPath ?? "").toLowerCase().includes(needle));
  }, [users, q]);
  const selectable = visible.filter((u) => u.status !== "existing");
  const allVisibleSelected = selectable.length > 0 && selectable.every((u) => selected.has(u.email));
  const counts = {
    total: users?.length ?? 0,
    new: users?.filter((u) => u.status === "new").length ?? 0,
    existing: users?.filter((u) => u.status === "existing").length ?? 0,
    suspended: users?.filter((u) => u.status === "suspended").length ?? 0,
  };

  const toggle = (email: string) =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });

  return (
    <Dialog
      open
      onClose={onClose}
      size="xl"
      title={`Import users from ${status.connectorName ?? "Google Workspace"}`}
      busy={busy}
      cancel={result ? false : undefined}
      primary={
        result ? (
          <Button variant="primary" onClick={onClose}>Done</Button>
        ) : (
          <Button
            variant="primary"
            disabled={busy || !users || selected.size === 0}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                setResult(await post<ImportResult>("/api/integrations/google/directory/import", { emails: [...selected] }));
                onDone();
              } catch (err) {
                setError((err as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? "Importing…" : `Import ${selected.size} user${selected.size === 1 ? "" : "s"}`}
          </Button>
        )
      }
    >
      {result ? (
        <div className="space-y-3 text-sm">
          <p className="rounded-md bg-success/10 px-3 py-2 text-success">
            Imported {result.created.length} user{result.created.length === 1 ? "" : "s"} as members.
          </p>
          {result.skippedExisting.length > 0 && <p className="text-muted">Skipped {result.skippedExisting.length} already in AgentOS.</p>}
          {result.skippedNotFound.length > 0 && <p className="text-warning">Not found in the directory anymore: {result.skippedNotFound.join(", ")}</p>}
        </div>
      ) : error ? (
        <ErrorText error={error} />
      ) : !users ? (
        <p className="text-sm text-muted">Loading the Google Workspace directory…</p>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Badge>{counts.total} in directory</Badge>
            <Badge tone="emerald">{counts.new} new</Badge>
            <Badge>{counts.existing} already added</Badge>
            {counts.suspended > 0 && <Badge tone="amber">{counts.suspended} suspended</Badge>}
          </div>
          <input className={inputCls} placeholder="Search name, email or org unit" value={q} onChange={(e) => setQ(e.target.value)} />
          <Table
            rows={visible}
            rowKey={(u) => u.email}
            rowClassName={(u) => (u.status === "existing" ? "opacity-60" : "")}
            empty="No users match."
            columns={[
              {
                header: (
                  <span className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      aria-label="Select all"
                      checked={allVisibleSelected}
                      onChange={(e) =>
                        setSelected((cur) => {
                          const next = new Set(cur);
                          for (const u of selectable) e.target.checked ? next.add(u.email) : next.delete(u.email);
                          return next;
                        })
                      }
                    />
                    User
                  </span>
                ),
                primary: true,
                cell: (u) => (
                  <div className="flex items-center gap-3">
                    <input type="checkbox" disabled={u.status === "existing"} checked={selected.has(u.email)} onChange={() => toggle(u.email)} aria-label={`Select ${u.email}`} />
                    {u.photoUrl ? <img src={u.photoUrl} alt="" className="size-6 rounded-full" referrerPolicy="no-referrer" /> : <span className="size-6 rounded-full bg-fill-strong" />}
                    <div className="min-w-0">
                      <div className="truncate font-medium">{u.name} {u.isAdmin && <Badge tone="blue">admin</Badge>}</div>
                      <div className="truncate text-xs text-muted">{u.email}</div>
                    </div>
                  </div>
                ),
              },
              { header: "Org unit", className: "text-xs text-muted", cell: (u) => u.orgUnitPath ?? "—" },
              { header: "Last login", className: "text-xs text-muted", cell: (u) => (u.lastLoginTime ? timeAgo(u.lastLoginTime) : "never") },
              { header: "Status", cell: (u) => (u.status === "new" ? <Badge tone="emerald">new</Badge> : u.status === "existing" ? <Badge>already added</Badge> : <Badge tone="amber">suspended</Badge>) },
            ]}
          />
          <p className="text-xs text-muted">Selected users are created as <b>members</b>. Grant them agents after importing.</p>
        </div>
      )}
    </Dialog>
  );
}

function HrSync({ status, onDone, onClose }: { status: HrStatus; onDone: () => void; onClose: () => void }) {
  const [people, setPeople] = useState<HrPerson[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [q, setQ] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<HrSyncResult | null>(null);

  useEffect(() => {
    post<{ people: HrPerson[] }>("/api/integrations/bamboohr/directory/preview").then(
      (r) => {
        setPeople(r.people);
        setSelected(new Set(r.people.filter((p) => p.status === "new").map((p) => p.email)));
      },
      (err) => setError((err as Error).message),
    );
  }, []);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (people ?? []).filter((p) => !needle || [p.email, p.name, p.jobTitle, p.department].some((v) => (v ?? "").toLowerCase().includes(needle)));
  }, [people, q]);
  const counts = {
    total: people?.length ?? 0,
    new: people?.filter((p) => p.status === "new").length ?? 0,
    update: people?.filter((p) => p.status === "update").length ?? 0,
    unchanged: people?.filter((p) => p.status === "unchanged").length ?? 0,
  };

  return (
    <Dialog
      open
      onClose={onClose}
      size="xl"
      title={`Sync users from ${status.connectorName ?? "BambooHR"}`}
      busy={busy}
      cancel={result ? false : undefined}
      primary={
        result ? (
          <Button variant="primary" onClick={onClose}>Done</Button>
        ) : (
          <Button
            variant="primary"
            disabled={busy || !people || (selected.size === 0 && counts.update === 0 && counts.unchanged === 0)}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                setResult(await post<HrSyncResult>("/api/integrations/bamboohr/directory/sync", { importEmails: [...selected] }));
                onDone();
              } catch (err) {
                setError((err as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? "Syncing…" : `Import ${selected.size} · update ${counts.update}`}
          </Button>
        )
      }
    >
      {result ? (
        <div className="space-y-2 text-sm">
          <p className="rounded-md bg-success/10 px-3 py-2 text-success">
            Imported {result.created.length} · updated {result.updated.length} · {result.unchanged} already up to date.
          </p>
          <p className="text-muted">Profiles refresh automatically once a day. Agents see each person's job title, department, location and manager.</p>
        </div>
      ) : error ? (
        <ErrorText error={error} />
      ) : !people ? (
        <p className="text-sm text-muted">Loading the BambooHR directory…</p>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Badge>{counts.total} employees</Badge>
            <Badge tone="emerald">{counts.new} not in Pandas</Badge>
            <Badge tone="blue">{counts.update} to update</Badge>
            <Badge>{counts.unchanged} up to date</Badge>
          </div>
          <input className={inputCls} placeholder="Search name, email, title or department" value={q} onChange={(e) => setQ(e.target.value)} />
          <List empty="No employees match.">
            {visible.map((p) => (
              <label key={p.email} className={`flex items-start gap-3 ${listRowCls} ${p.status === "new" ? "cursor-pointer" : ""}`}>
                <input
                  type="checkbox"
                  className="mt-1"
                  disabled={p.status !== "new"}
                  checked={p.status === "new" ? selected.has(p.email) : true}
                  aria-label={`Import ${p.email}`}
                  onChange={() =>
                    setSelected((cur) => {
                      const next = new Set(cur);
                      next.has(p.email) ? next.delete(p.email) : next.add(p.email);
                      return next;
                    })
                  }
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2">
                    <span className="font-medium">{p.name}</span>
                    {p.status === "new" ? <Badge tone="emerald">new</Badge> : p.status === "update" ? <Badge tone="blue">update {p.changes.join(", ")}</Badge> : <Badge>up to date</Badge>}
                  </div>
                  <div className="truncate text-xs text-muted">{p.email}</div>
                  <div className="text-xs text-muted">
                    {[p.jobTitle, p.department, p.location].filter(Boolean).join(" · ") || "—"}
                    {p.manager && <span className="text-faint"> · reports to {p.manager}</span>}
                  </div>
                </div>
              </label>
            ))}
          </List>
          <p className="text-xs text-muted">Existing users are always enriched. Ticked employees are created as <b>members</b>.</p>
        </div>
      )}
    </Dialog>
  );
}

type GithubStatus = { available: boolean; connectorId: string | null; connectorName: string | null; org: string | null; reason: string | null };
type GithubMatch = {
  userId: string; name: string; email: string; currentGithub: string | null; suggestion: string | null;
  matchedBy: "saml" | "commit_email" | "public_email" | "email_prefix" | "name" | null; githubName: string | null; avatarUrl: string | null;
};
type GithubPreview = { org: string; connectorName: string; memberCount: number; matches: GithubMatch[]; unmatchedMembers: { login: string; name: string | null }[] };

const MATCH_LABEL: Record<NonNullable<GithubMatch["matchedBy"]>, { label: string; strong: boolean }> = {
  saml: { label: "SSO identity", strong: true },
  commit_email: { label: "commit email", strong: true },
  public_email: { label: "public email", strong: true },
  email_prefix: { label: "email name = login", strong: false },
  name: { label: "same name", strong: false },
};

function GithubUsernames({ onDone, onClose }: { onDone: () => void; onClose: () => void }) {
  const [preview, setPreview] = useState<GithubPreview | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ updated: string[]; errors: { userId: string; error: string }[] } | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    post<GithubPreview>("/api/integrations/github/usernames/preview").then(
      (p) => {
        setPreview(p);
        setValues(Object.fromEntries(p.matches.map((m) => [m.userId, m.suggestion ?? m.currentGithub ?? ""])));
        setSelected(new Set(p.matches.filter((m) => m.suggestion && m.suggestion !== m.currentGithub).map((m) => m.userId)));
      },
      (err) => setError((err as Error).message),
    );
  }, []);

  const rows = (preview?.matches ?? []).filter((m) => showAll || (m.suggestion && m.suggestion !== m.currentGithub) || !m.currentGithub);
  const names = new Map((preview?.matches ?? []).map((m) => [m.userId, m.name]));

  return (
    <Dialog
      open
      onClose={onClose}
      size="xl"
      title={preview ? `GitHub usernames from ${preview.org}` : "GitHub usernames"}
      busy={busy}
      cancel={result ? false : undefined}
      primary={
        result ? (
          <Button variant="primary" onClick={onClose}>Done</Button>
        ) : (
          <Button
            variant="primary"
            disabled={busy || !preview || selected.size === 0}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                const assignments = [...selected].map((userId) => ({ userId, githubUsername: values[userId]?.trim() || null }));
                setResult(await post("/api/integrations/github/usernames/apply", { assignments }));
                onDone();
              } catch (err) {
                setError((err as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? "Saving…" : `Save ${selected.size} username${selected.size === 1 ? "" : "s"}`}
          </Button>
        )
      }
    >
      {result ? (
        <div className="space-y-2 text-sm">
          <p className="rounded-md bg-success/10 px-3 py-2 text-success">Saved {result.updated.length} GitHub username{result.updated.length === 1 ? "" : "s"}.</p>
          {result.errors.map((e) => (
            <p key={e.userId} className="text-danger">{names.get(e.userId)}: {e.error}</p>
          ))}
        </div>
      ) : error ? (
        <ErrorText error={error} />
      ) : !preview ? (
        <p className="text-sm text-muted">Reading {`the GitHub organization`} — members, SSO identities and recent commits…</p>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Badge>{preview.memberCount} org members</Badge>
            <Badge tone="emerald">{preview.matches.filter((m) => m.suggestion && m.suggestion !== m.currentGithub).length} suggestions</Badge>
            <Badge>{preview.matches.filter((m) => m.currentGithub).length} already set</Badge>
            <Switch size="sm" className="ml-auto" label="Show everyone" checked={showAll} onChange={setShowAll} />
          </div>
          <List empty={<>Everyone already has a GitHub username. Tick "Show everyone" to review.</>}>
            {rows.map((m) => {
              const how = m.matchedBy ? MATCH_LABEL[m.matchedBy] : null;
              return (
                <div key={m.userId} className={`flex flex-wrap items-center gap-x-3 gap-y-2 ${listRowCls} sm:flex-nowrap`}>
                  <input
                    type="checkbox"
                    aria-label={`Save for ${m.email}`}
                    checked={selected.has(m.userId)}
                    onChange={() =>
                      setSelected((cur) => {
                        const next = new Set(cur);
                        next.has(m.userId) ? next.delete(m.userId) : next.add(m.userId);
                        return next;
                      })
                    }
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{m.name}</div>
                    <div className="truncate text-xs text-muted">{m.email}{m.currentGithub ? ` · now @${m.currentGithub}` : ""}</div>
                  </div>
                  <div className="flex w-full items-center gap-2 sm:w-72">
                    {m.avatarUrl ? <img src={m.avatarUrl} alt="" className="size-6 rounded-full" /> : <span className="size-6 shrink-0 rounded-full bg-fill-strong" />}
                    <input
                      className={`${inputCls} font-mono`}
                      placeholder="github-username"
                      value={values[m.userId] ?? ""}
                      onChange={(e) => {
                        setValues({ ...values, [m.userId]: e.target.value });
                        setSelected((cur) => new Set(cur).add(m.userId));
                      }}
                    />
                  </div>
                  <div className="w-full text-xs sm:w-36 sm:text-right">
                    {how ? <Badge tone={how.strong ? "emerald" : "amber"}>{how.label}</Badge> : <span className="text-faint">no match</span>}
                    {m.githubName && <div className="truncate text-muted">{m.githubName}</div>}
                  </div>
                </div>
              );
            })}
          </List>
          {preview.unmatchedMembers.length > 0 && (
            <p className="text-xs text-muted">
              Org members not matched to anyone: {preview.unmatchedMembers.map((m) => `@${m.login}${m.name ? ` (${m.name})` : ""}`).join(", ")}. Type one into a row above to assign it.
            </p>
          )}
          <p className="text-xs text-muted">Green matches are reliable; amber ones (same name, email name) are worth a quick check.</p>
        </div>
      )}
    </Dialog>
  );
}

function ProfileEditor({ user, onSaved, onClose }: { user: UserWithAccess; onSaved: () => void; onClose: () => void }) {
  const [form, setForm] = useState({
    jobTitle: user.jobTitle ?? "", department: user.department ?? "", location: user.location ?? "", manager: user.manager ?? "",
    githubUsername: user.githubUsername ?? "",
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Profile · ${user.name}`}
      busy={busy}
      primary={
        <Button
          variant="primary"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await put(`/api/users/${user.id}/profile`, form);
              onSaved();
              onClose();
            } catch (err) {
              setError((err as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          Save profile
        </Button>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-muted">
          Agents read this every time {user.name} asks something, so answers fit their role.
          {user.profileSource === "bamboohr" && user.profileSyncedAt && ` Job fields sync from BambooHR (last ${timeAgo(user.profileSyncedAt)}) and may be overwritten there.`}
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Job title"><input className={inputCls} value={form.jobTitle} onChange={set("jobTitle")} /></Field>
          <Field label="Department"><input className={inputCls} value={form.department} onChange={set("department")} /></Field>
          <Field label="Location"><input className={inputCls} value={form.location} onChange={set("location")} /></Field>
          <Field label="Manager"><input className={inputCls} value={form.manager} onChange={set("manager")} /></Field>
          <Field label="GitHub username" hint="Their GitHub login, e.g. octocat — usually different from their work email.">
            <input className={`${inputCls} font-mono`} value={form.githubUsername} onChange={set("githubUsername")} placeholder="octocat" autoComplete="off" spellCheck={false} />
          </Field>
        </div>
        {/* What they own and care about is now their own note, which they write and agents add to: Memory → User notes. */}
        <p className="text-xs text-muted">
          How to work with them lives in their note, which they write themselves and agents add to:{" "}
          <TextLink to="/admin/notes">
            Memory → User notes
          </TextLink>
          .
        </p>
        <ErrorText error={error} />
      </div>
    </Dialog>
  );
}

function NewUser({ onCreated, onClose }: { onCreated: () => void; onClose: () => void }) {
  const [form, setForm] = useState({ email: "", name: "", role: "member" as "admin" | "member", jobTitle: "", department: "", kind: "person" as "person" | "service" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const service = form.kind === "service";
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      // A service account — a script's or an automation's principal — has a name and a role, and an address nobody mails.
      const user = await post<UserDTO>("/api/users", service ? { name: form.name, role: form.role, kind: "service" } : { email: form.email, name: form.name, role: form.role });
      if (!service && (form.jobTitle.trim() || form.department.trim())) await put(`/api/users/${user.id}/profile`, { jobTitle: form.jobTitle, department: form.department });
      onCreated();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title="New user"
      busy={busy}
      primary={
        <Button variant="primary" disabled={busy || (!service && !form.email.trim()) || !form.name.trim()} onClick={submit}>
          {busy ? "Adding…" : service ? "Add service account" : "Add user"}
        </Button>
      }
    >
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field label="Kind" hint="A person signs in with Google or a token of their own. A service account is a script's or an automation's principal: it reads and configures through the admin API as its role allows, never owns an agent, is never asked and never decides, and holds no conversation.">
          <select className={inputCls} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as "person" | "service" })}>
            <option value="person">Person</option>
            <option value="service">Service account</option>
          </select>
        </Field>
        {!service && (
        <Field label="Email">
          <input className={inputCls} type="email" autoFocus value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@pandas.io" />
        </Field>
        )}
        <Field label="Name">
          <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Role">
          <select className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as "admin" | "member" })}>
            <option value="member">Member — uses agents they are granted</option>
            <option value="admin">Admin — manages the platform; uses the agents open to admins</option>
          </select>
        </Field>
        {!service && (<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Job title (optional)"><input className={inputCls} value={form.jobTitle} onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} /></Field>
          <Field label="Department (optional)"><input className={inputCls} value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></Field>
        </div>)}
        <ErrorText error={error} />
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

function RoleSelect({ user, onChanged }: { user: UserWithAccess; onChanged: () => void }) {
  return (
    <select
      aria-label={`Role for ${user.email}`}
      value={user.role}
      onChange={async (e) => {
        const role = e.target.value as "admin" | "member";
        const sure = await confirmDialog(
          role === "admin"
            ? { title: `Make ${user.name} an admin?`, body: "Admins can manage agents, connectors, users and see all conversations.", confirm: "Make admin" }
            : { title: `Remove admin rights from ${user.name}?`, body: "They keep their own chats and connections, as a member.", confirm: "Remove admin rights", danger: true },
        );
        if (!sure) {
          e.target.value = user.role;
          return;
        }
        try {
          await put(`/api/users/${user.id}/role`, { role });
        } catch (err) {
          await alertDialog({ title: "The role did not change", body: (err as Error).message });
        }
        onChanged();
      }}
      className={`rounded-full border-0 px-2.5 py-1 text-xs font-medium ring-1 ${user.role === "admin" ? "bg-haze-soft text-slate ring-haze-deep/40 dark:bg-haze/15 dark:text-haze" : "bg-surface text-muted ring-line"}`}
    >
      <option value="member">Member</option>
      <option value="admin">Admin</option>
    </select>
  );
}

const brandOf = (c: ConnectorDTO) => (typeof c.authConfig.catalog === "string" ? c.authConfig.catalog : c.slug);

/** Which connectors a person may use through Pandito and its agents, and at which data level. */
function ConnectorAccessEditor({ user, connectors, onChange, onClose }: {
  user: UserWithAccess;
  connectors: ConnectorDTO[];
  onChange: (access: Record<string, string>) => void;
  onClose: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const isAdmin = user.role === "admin";
  const set = async (connectorId: string, level: string | null) => {
    setBusy(connectorId);
    setError(null);
    try {
      const r = await put<{ connectorAccess: Record<string, string> }>(`/api/users/${user.id}/connector-access`, { connectorId, level });
      onChange(r.connectorAccess);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const enabled = connectors.filter((c) => c.enabled);
  const levelName = (c: ConnectorDTO, level: string | undefined) =>
    level && level !== "default" ? (c.levels.find((l) => l.key === level)?.name ?? level) : `Default (${policySummary(c.policy) ?? "no restrictions"})`;
  return (
    <Dialog open onClose={onClose} size="lg" title={`Connector access · ${user.name}`} primary={<Button variant="primary" onClick={onClose}>Done</Button>} cancel={false}>
      <div className="space-y-3">
        <List
          hint={
            isAdmin
              ? `${user.name} is an admin. The connectors switched on here are the ones their requests may reach — being an admin grants none by itself. Data levels still apply.`
              : `${user.name} talks to Pandito, which can use these connectors or delegate to an agent that has them. Everything else stays out of reach for ${user.name.split(" ")[0]}'s requests, in the web app, Slack and email.`
          }
          empty="No connectors yet. Add them in Connectors."
        >
          {enabled.map((c) => {
            const level = user.connectorAccess[c.id];
            const on = level !== undefined;
            return (
              <ListRow
                key={c.id}
                leading={<BrandTile brand={brandOf(c)} />}
                title={<span className="truncate text-sm font-medium text-fg">{c.name}</span>}
                subtitle={on ? (c.levels.length ? `Data level: ${levelName(c, level)}` : "Has access") : "No access"}
                trailing={
                  <>
                    {on && c.levels.length > 0 && (
                      <select
                        aria-label={`Data level for ${c.name}`}
                        className={`${inputCls} w-full py-1.5 text-xs sm:w-52`}
                        value={level ?? "default"}
                        disabled={busy === c.id}
                        onChange={(e) => void set(c.id, e.target.value)}
                      >
                        <option value="default">Default level</option>
                        {c.levels.map((l) => (
                          <option key={l.key} value={l.key}>
                            {l.name}
                            {l.autoAssignAdmins ? " (admins get this)" : ""}
                          </option>
                        ))}
                      </select>
                    )}
                    <Switch
                      aria-label={`${on ? "Remove" : "Give"} access to ${c.name}`}
                      checked={on}
                      disabled={busy === c.id}
                      onChange={() => void set(c.id, on ? null : "default")}
                    />
                  </>
                }
              />
            );
          })}
        </List>
        <ErrorText error={error} />
      </div>
    </Dialog>
  );
}

/** Brand marks of the connectors a person may use; opens the editor. */
function AccessSummary({ user, connectors, onOpen }: { user: UserWithAccess; connectors: ConnectorDTO[]; onOpen: () => void }) {
  const enabled = connectors.filter((c) => c.enabled);
  const granted = enabled.filter((c) => user.connectorAccess[c.id] !== undefined);
  const elevated = granted.filter((c) => (user.connectorAccess[c.id] ?? "default") !== "default").length;
  return (
    <button
      type="button"
      onClick={onOpen}
      title="Edit connector access"
      className="group flex max-w-full min-w-0 items-center gap-2 rounded-full py-1 pr-2.5 pl-1 text-left text-xs transition hover:bg-fill"
    >
      {granted.length === 0 ? (
        <span className="rounded-full bg-warning/10 px-2 py-0.5 font-medium text-warning">No connectors</span>
      ) : (
        <>
          <span className="flex shrink-0 -space-x-1.5">
            {granted.slice(0, 5).map((c) => (
              <span key={c.id} title={c.name} className="grid size-6 place-items-center rounded-full bg-raised shadow-sm ring-2 ring-canvas">
                <BrandIcon brand={brandOf(c)} className="size-3.5" />
              </span>
            ))}
          </span>
          <span className="min-w-0 truncate text-muted">
            {user.role === "admin" ? "All connectors" : `${granted.length} ${granted.length === 1 ? "connector" : "connectors"}`}
            {elevated > 0 && <span className="text-warning"> · {elevated} elevated</span>}
          </span>
        </>
      )}
      <span className="shrink-0 text-faint opacity-0 transition group-hover:opacity-100">Edit</span>
    </button>
  );
}

/** What a person's runs cost, under their activity. A person with no runs shows nothing. */
function SpendLine({ spend }: { spend: PersonSpendDTO | undefined }) {
  if (!spend) return null;
  return (
    <div className="flex items-center gap-1 text-tiny text-faint tabular-nums">
      <span>${spend.costUsd.toFixed(2)} in 30 days</span>
      <CostBreakdown billedUsd={spend.billedUsd} estimatedUsd={spend.estimatedUsd} />
    </div>
  );
}

export function AdminUsers() {
  const [users, setUsers] = useState<UserWithAccess[]>([]);
  const [connectors, setConnectors] = useState<ConnectorDTO[]>([]);
  const [spend, setSpend] = useState<Record<string, PersonSpendDTO>>({});
  const [editingAccess, setEditingAccess] = useState<string | null>(null);
  const [me, setMe] = useState<{ id: string } | null>(null);
  // A link may arrive searched (a connector's Access tab names a person by their address).
  const [q, setQ] = useState(() => new URLSearchParams(location.search).get("q") ?? "");
  const [directory, setDirectory] = useState<DirectoryStatus | null>(null);
  const [importing, setImporting] = useState(false);
  const [hr, setHr] = useState<HrStatus | null>(null);
  const [hrSyncing, setHrSyncing] = useState(false);
  const [github, setGithub] = useState<GithubStatus | null>(null);
  const [matchingGithub, setMatchingGithub] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingProfile, setEditingProfile] = useState<UserWithAccess | null>(null);
  const [editingChannels, setEditingChannels] = useState<UserWithAccess | null>(null);
  const [editingTokens, setEditingTokens] = useState<UserWithAccess | null>(null);
  const [live, setLive] = useState<PresenceDTO[]>([]);

  const load = () => {
    get<UserWithAccess[]>("/api/users").then(setUsers, () => {});
    get<ConnectorDTO[]>("/api/connectors").then(setConnectors, () => {});
    get<Record<string, PersonSpendDTO>>("/api/users/stats").then(setSpend, () => {});
  };
  useEffect(() => {
    load();
    get<{ user: { id: string } }>("/api/me").then((r) => setMe(r.user), () => {});
    get<DirectoryStatus>("/api/integrations/google/directory").then(setDirectory, () => {});
    get<HrStatus>("/api/integrations/bamboohr/directory").then(setHr, () => {});
    get<GithubStatus>("/api/integrations/github/usernames").then(setGithub, () => {});
    const presence = () => get<PresenceDTO[]>("/api/presence").then(setLive, () => {});
    presence();
    const timer = setInterval(presence, 20_000);
    return () => clearInterval(timer);
  }, []);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return users.filter((u) => !needle || [u.name, u.email, u.jobTitle, u.department].some((v) => (v ?? "").toLowerCase().includes(needle)));
  }, [users, q]);

  const setAccess = (userId: string) => (connectorAccess: Record<string, string>) => setUsers((cur) => cur.map((x) => (x.id === userId ? { ...x, connectorAccess } : x)));
  const accessUser = users.find((u) => u.id === editingAccess) ?? null;
  const remove = async (u: UserWithAccess) => {
    if (!(await confirmDialog({ title: `Delete ${u.name}?`, body: `${u.email} loses access immediately and is signed out. Their conversations and logs are kept for the audit trail.`, confirm: "Delete user", danger: true }))) return;
    try {
      await del(`/api/users/${u.id}`);
    } catch (err) {
      await alertDialog({ title: `${u.name} was not deleted`, body: (err as Error).message });
    }
    load();
  };

  const googleAction =
    directory?.connectorId &&
    (directory.available ? (
      <Button onClick={() => setImporting(true)}>
        <span className="mr-1.5">G</span>Import from Google
      </Button>
    ) : (
      <ButtonLink to="/admin/connectors" title={directory.reason ?? ""}>
        Reconnect Google to import users
      </ButtonLink>
    ));

  const activity = (u: UserWithAccess) => {
    const isLive = live.some((p) => p.id === u.id);
    return (
      <div className="min-w-0">
        <div className={`flex items-center gap-1.5 text-xs ${isLive ? "font-medium text-success" : "text-muted"}`}>
          {isLive && <span className="size-2 shrink-0 rounded-full bg-success" />}
          {isLive ? "Live now" : u.lastSeenAt ? `Active ${timeAgo(u.lastSeenAt)}` : "Never signed in"}
        </div>
        {u.lastLoginAt && <div className="truncate text-tiny text-faint">Signed in {timeAgo(u.lastLoginAt)}</div>}
      </div>
    );
  };

  const profileLine = (u: UserWithAccess) =>
    (u.jobTitle || u.department || u.githubUsername) && (
      <div className="truncate text-xs text-muted">{[u.jobTitle, u.department, u.githubUsername && `GitHub @${u.githubUsername}`].filter(Boolean).join(" · ")}</div>
    );

  const rowActions = (u: UserWithAccess) => (
    <div className="flex items-center gap-1 text-xs">
      {u.kind === "person" && (
        <Button variant="ghost" size="sm" className="whitespace-nowrap" onClick={() => setEditingProfile(u)}>
          {u.jobTitle || u.department ? "Profile" : "Add profile"}
        </Button>
      )}
      {u.kind === "person" && (
        <Button variant="ghost" size="sm" className="whitespace-nowrap" onClick={() => setEditingChannels(u)}>
          Channels
        </Button>
      )}
      {/* A person's tokens are their own, on About me; an admin manages a service account's (D15). */}
      {u.kind === "service" && (
        <Button variant="ghost" size="sm" className="whitespace-nowrap" onClick={() => setEditingTokens(u)}>
          Tokens
        </Button>
      )}
      {u.auth0Bound && (
        <Button
          variant="ghost"
          size="sm"
          className="whitespace-nowrap"
          title="Their account is bound to the Google identity they first signed in with. Reset it when the mailbox has changed hands; the next Google sign-in binds again."
          onClick={async () => {
            if (!(await confirmDialog({ title: `Reset the Google identity bound to ${u.name}?`, body: "Their next Google sign-in binds the account again.", confirm: "Reset identity", danger: true }))) return;
            try {
              await post(`/api/users/${u.id}/reset-subject`, {});
            } catch (err) {
              await alertDialog({ title: "The identity was not reset", body: (err as Error).message });
            }
            void load();
          }}
        >
          Reset Google identity
        </Button>
      )}
      {me?.id !== u.id && (
        <Button variant="danger" size="sm" className="whitespace-nowrap" onClick={() => void remove(u)}>
          Delete
        </Button>
      )}
    </div>
  );

  return (
    <div>
      <PageHeader
        title="Users"
        subtitle="Everyone talks to Pandito, which delegates to agents. What it can reach for each person depends on the connectors switched on in their row — admins included: being an admin grants none by itself."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" onClick={() => setCreating(true)}>New user</Button>
            {hr?.available && (
              <Button onClick={() => setHrSyncing(true)} title={hr.lastSyncedAt ? `Last synced ${timeAgo(hr.lastSyncedAt)}` : "Not synced yet"}>
                <span className="inline-flex items-center gap-1.5"><BrandIcon brand="bamboohr" className="size-4" /> Sync BambooHR</span>
              </Button>
            )}
            {github?.available && <Button onClick={() => setMatchingGithub(true)} title={github.org ? `From github.com/${github.org}` : undefined}><span className="inline-flex items-center gap-1.5"><BrandIcon brand="github" className="size-4" /> Match GitHub usernames</span></Button>}
            {googleAction}
          </div>
        }
      />

      <div className="px-4 py-4 md:px-6">
        <input className={`${inputCls} mb-3 md:max-w-sm`} placeholder={`Search ${users.length} users by name, email, title or department`} value={q} onChange={(e) => setQ(e.target.value)} />

        <Table
          rows={visible}
          rowKey={(u) => u.id}
          empty="No users match."
          columns={[
            {
              header: "Person",
              primary: true,
              cell: (u) => (
                <div className="flex min-w-0 items-center gap-3">
                  <Avatar name={u.name} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 truncate text-sm font-medium">{u.name}{u.kind === "service" && <Badge tone="violet">Service</Badge>}</div>
                    <div className="truncate text-xs text-muted">{u.kind === "service" ? "Service account — signs in with a token only" : u.email}</div>
                    {profileLine(u)}
                  </div>
                </div>
              ),
            },
            { header: "Role", cell: (u) => <RoleSelect user={u} onChanged={load} /> },
            { header: "Connector access", className: "lg:max-w-64", cell: (u) => <AccessSummary user={u} connectors={connectors} onOpen={() => setEditingAccess(u.id)} /> },
            { header: "Activity", cell: (u) => <div>{activity(u)}<SpendLine spend={spend[u.id]} /></div> },
            { header: "Actions", align: "right", cell: (u) => rowActions(u) },
          ]}
        />
      </div>

      {creating && <NewUser onCreated={load} onClose={() => setCreating(false)} />}
      {importing && directory && <GoogleImport status={directory} onDone={load} onClose={() => setImporting(false)} />}
      {hrSyncing && hr && <HrSync status={hr} onDone={load} onClose={() => setHrSyncing(false)} />}
      {matchingGithub && <GithubUsernames onDone={load} onClose={() => setMatchingGithub(false)} />}
      {editingProfile && <ProfileEditor user={editingProfile} onSaved={load} onClose={() => setEditingProfile(null)} />}
      {editingTokens && (
        <Dialog open onClose={() => setEditingTokens(null)} size="xl" title={`Tokens — ${editingTokens.name}`} primary={<Button onClick={() => setEditingTokens(null)}>Done</Button>} cancel={false}>
          <p className="mb-3 text-xs text-muted">A token signs in as {editingTokens.name} with their role. It is shown once when made; revoking it signs out whatever used it.</p>
          <Tokens userId={editingTokens.id} />
        </Dialog>
      )}
      {editingChannels && (
        <Dialog
          open
          onClose={() => setEditingChannels(null)}
          title={`Connected channels · ${editingChannels.name}`}
          primary={<Button onClick={() => setEditingChannels(null)}>Close</Button>}
          cancel={false}
        >
          <p className="mb-3 text-sm text-muted">
            The accounts every door recognises as {editingChannels.name}. Each one was proven, which is why none can be typed in here: a
            linked account acts with their full access. Removing one only stops it being recognised.
          </p>
          <ConnectedChannels userId={editingChannels.id} manage />
        </Dialog>
      )}
      {accessUser && <ConnectorAccessEditor user={accessUser} connectors={connectors} onChange={setAccess(accessUser.id)} onClose={() => setEditingAccess(null)} />}
    </div>
  );
}
